// Books — the ledger you can see. Read-only journal, chart of accounts and
// trial balance. Posting amounts stay the ledger's natural sign (debit
// positive, credit negative). view.cashDelta is the only money-in-positive
// field on an entry; view.lines are displayed as the server wrote them.

import {
  ConsoleDataError,
  fleetQueueListCapFired,
  notPublishedReason,
  type AdminClient,
} from "../api.js";
import type {
  LedgerAccount,
  LedgerAccountsResponse,
  LedgerEntriesPage,
  LedgerEntry,
  LedgerHeader,
  LedgerIds,
  LedgerPosting,
  LedgerQuery,
  LedgerSearchHit,
  LedgerSearchQuery,
  LedgerSearchResponse,
} from "../console/types.js";
import { MONTH_NOT_LOADED_TEXT } from "../console/data-source.js";
import type { AccountPath, CompanySlug, QueueItem, StaffRole } from "../types.js";
import { csvField } from "../csv_field.js";
import { saveCsv } from "../ui/csv-download.js";
import { clear, h } from "../dom.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  banner,
  disclosure,
  drawer,
  emptyState,
  errorState,
  pageHeader,
  segmented,
  svgIcon,
} from "../ui/components.js";
import { renderCheckNow } from "../ui/check-now.js";
import { accountText, disambiguatedAccountLabels, slugText } from "../console/report-render.js";
import {
  accountLabel,
  countText,
  countedText,
  dateText,
  lineLabelText,
  monthText,
  pluralText,
  timeAgoText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { readerRole, reviewCards } from "../ui/facts.js";
import { advice } from "../ui/capabilities.js";
import { navLabel } from "../ui/sentences.js";
import { failureCopy } from "../ui/failure.js";
import { measuredSetOf } from "../ui/measured-claim.js";
import {
  NO_MATCHES_ANY_MONTH,
  NO_MATCHES_MONTHS_SEARCHED,
  NOT_EVERY_MONTH_SEARCHED,
  SEARCH_ALL_MONTHS_PROMPT,
  SEARCH_COULD_NOT_FINISH,
  SEARCH_NEWEST_MATCHES,
  SEARCH_OLDER_NOT_SEARCHED,
  SEARCHED_EVERY_MONTH,
  couldNotBeReadText,
} from "../ui/measured-claim.js";
import { decimalToCents, formatDecimalMoney } from "../ui/money.js";
import { navigateTo, takeNavParams, type NavView } from "../ui/nav-bus.js";
import {
  createPeriodControl,
  defaultPeriod,
  formatPeriodLabel,
  isValidPeriod,
  shiftPeriod,
} from "../ui/period-control.js";

export interface GeneralLedgerPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /**
   * The reader's role, as the shell holds it (owner decision 10: an unknown
   * role reads as the account manager, never the owner).
   */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** Test seam. Defaults to the real clock. */
  now?: () => Date;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): Books names an account, so it names
   * the company in a between-companies path instead of printing its key.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

export const JOURNAL_PAGE_SIZE = 50;
/** The ledger route's own maximum (contracts/books-api.yaml: pageSize <= 200). */
export const EXPORT_PAGE_SIZE = 200;
/**
 * rd15 A11 fix round 2 (reviewer M1): the most the drill's account filter may
 * add to a read's URL. The edge refuses a request URL past roughly 16 KB before
 * the Worker sees it, and that arrives here as an error state with no journal,
 * so the page checks what it is about to send. The rest of the ledger URL is a
 * month, a page and a page size: bounded and tiny. A chart of sixty accounts,
 * the case that used to 400, is about 2 KB.
 */
export const LEDGER_ACCOUNT_FILTER_MAX = 12_000;
/** Mirrors the Worker's per-read account bound (`ledger_view.ts` MAX_ACCOUNTS). */
export const LEDGER_ACCOUNTS_MAX = 300;
/** Said instead of a journal the request URL could not carry. */
export const TOO_MANY_ACCOUNTS_TEXT =
  "This figure covers more accounts than one list can hold. Pick one account in the filter, or press Show all accounts.";

/**
 * Whether the read a drill asks for fits in a request URL. One account is
 * always short (the route allows 200 characters); a drilled set can name a
 * whole chart, so the list is what grows.
 */
export function ledgerAccountFilterFits(query: LedgerQuery): boolean {
  const accounts = query.accounts ?? (query.account === undefined ? [] : [query.account]);
  if (accounts.length === 0) return true;
  if (accounts.length > LEDGER_ACCOUNTS_MAX) return false;
  return (
    new URLSearchParams({ account: accounts.join(",") }).toString().length <=
    LEDGER_ACCOUNT_FILTER_MAX
  );
}

/**
 * A month the publisher accepts tops out near 1,650 entries; this is a
 * fail-closed ceiling for the download loop, not a page size. If a month ever
 * exceeds it the console stops and says so rather than making a partial file.
 */
export const EXPORT_MAX_ENTRIES = 20_000;
export const BOOKS_STALE_MS = 24 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;

const GROUP_ORDER = ["Assets", "Liabilities", "Income", "Expenses", "Equity", "Other"] as const;
type AccountGroup = (typeof GROUP_ORDER)[number];

const DECIMAL_AMOUNT = /^-?\d+(\.\d+)?$/;

export interface JournalCsvPosting {
  account: string;
  amount: string;
  location: string;
}

export interface JournalCsvRow {
  date: string;
  entryId?: string;
  headline: string;
  subline: string;
  cashWords: string;
  cashDelta: string;
  cashAccount: string;
  source: string;
  lines: string;
  postings?: readonly JournalCsvPosting[];
}

interface ShownEntry extends JournalCsvRow {
  entry: LedgerEntry;
  accountAmount?: string;
  balanceAfter?: string;
}

type BooksView = NavView;
type SearchScope = "month" | "all";

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export type BooksFailureKind =
  | { kind: "not-published" }
  | { kind: "not-enabled" }
  | { kind: "statements" }
  | { kind: "plain"; sentence: string };

/**
 * The state a failed books read is in. The words for the plain case come from
 * `failureCopy` (the one home, item B4b-2), so this page no longer carries its
 * own 401/403/429 sentences and never prints raw server text.
 */
export function classifyBooksFailure(error: unknown, role: StaffRole): BooksFailureKind {
  if (error instanceof ConsoleDataError) {
    const reason = notPublishedReason(error);
    if (reason === "ledger_not_published") return { kind: "not-published" };
    if (reason === "ledger_view_not_enabled") return { kind: "not-enabled" };
    if (reason === "statements_unavailable") return { kind: "statements" };
  }
  // A read: it could not load. Never worded as a write, never the server's own
  // words (decision 1 gives the owner 403 its own sentence).
  return { kind: "plain", sentence: failureCopy(error, { role, context: "read" }).sentence };
}

/**
 * The words for a company whose books view is switched on nowhere yet. Books
 * and Reports both show this state, so it has ONE owner here (the
 * duplicate-sentence guard's rule for page words two files of one group share).
 */
export const BOOKS_VIEW_OFF_TEXT = "The Books view is not switched on for this company yet.";

export function accountGroup(type: string): AccountGroup {
  const folded = type.trim().toLowerCase();
  if (folded === "asset" || folded === "assets") return "Assets";
  if (folded === "liability" || folded === "liabilities") return "Liabilities";
  if (folded === "income") return "Income";
  if (folded === "expense" || folded === "expenses") return "Expenses";
  if (folded === "equity") return "Equity";
  return "Other";
}

export function flipsSign(type: string): boolean {
  const group = accountGroup(type);
  return group === "Income" || group === "Liabilities" || group === "Equity";
}

/**
 * The natural side of a group: which way its balance counts. Shown in the
 * Accounts column header so a positive number reads correctly.
 */
export function countsAsSide(group: AccountGroup): "debit" | "credit" | null {
  if (group === "Assets" || group === "Expenses") return "debit";
  if (group === "Income" || group === "Liabilities" || group === "Equity") return "credit";
  return null;
}

/**
 * A closing balance as a person states it (rd Y3): Income, Liabilities and
 * Equity read positive, the convention QuickBooks uses; Assets and Expenses are
 * already debit-positive and are unchanged. Only the label changes — the
 * `data-*` attributes keep the ledger's own signed number.
 */
export function shownBalance(amount: string, type: string): string {
  if (!flipsSign(type)) return amount;
  const cents = decimalToCents(amount);
  if (cents === null) return amount;
  return centsToDecimal(-cents);
}

export function showsActivityColumn(type: string): boolean {
  const group = accountGroup(type);
  return group === "Income" || group === "Expenses";
}

export function formatLedgerMoney(amount: string): string {
  return formatDecimalMoney(amVisibleDisplayText(amount));
}

/** Humanised line amounts use the same one minus style and separators. */
export function signedDollars(amount: string): string {
  return formatDecimalMoney(amVisibleDisplayText(amount));
}

export function cashEffectWords(delta: string | undefined): string {
  if (delta === undefined || delta === "") return "";
  const clean = amVisibleDisplayText(delta);
  if (clean.startsWith("-")) return `Out ${formatLedgerMoney(clean.slice(1))}`;
  return `In ${formatLedgerMoney(clean)}`;
}

export function plainAccountLabel(
  account: string,
  companies: AccountLabelContext,
): string {
  if (account.trim() === "") return "";
  // The ledger body already sits under its type heading, so the head is
  // dropped and the segments are joined with a middot. The words come from
  // `accountLabel` (rd Y0): one account, one spelling, everywhere.
  return accountLabel(account, companies, { separator: " \u00b7 ", dropLeading: "ledgerRoot" });
}

export function plaidIdOrEmpty(ids: LedgerIds): string {
  const id = ids.plaidId?.trim() ?? "";
  if (id === "" || id.toLowerCase() === "none") return "";
  return id;
}

/**
 * Chip text for a free-form ledger source.
 *
 * Specific answers are matched before the broader "moov" / plaid-id rules.
 * Applied in the order written in the brief, "moov-approved-*" would always
 * say Moov and every rule-* row (they carry a plaid id) would say Bank, so
 * those chips could never appear on a real ledger.
 */
export function sourceChipLabel(source: string, ids: LedgerIds): string {
  const folded = source.toLowerCase();
  if (folded.startsWith("queue-approved") || folded.startsWith("moov-approved")) {
    // rd R8-02: the chip used to say "You answered" to every viewer. The
    // ledger entry carries no actor, so the console cannot know the viewer
    // gave the answer; it names what it does know.
    return "By a person";
  }
  // rd R8-16: the decided word for a wallet source (rd-polish-rules §1).
  if (folded.startsWith("moov")) return "Wallet payment";
  // Owner grant, 2026-11-02: one entry, two of the company's own accounts. The
  // chip says what the money did, not which writer booked it — and never the
  // stored value (`own-transfer`), which is a slug.
  if (folded === "own-transfer") return "Your own accounts";
  if (folded.startsWith("rule")) return "Rule";
  if (folded.startsWith("plaid") || folded.startsWith("bank") || plaidIdOrEmpty(ids) !== "") {
    return "Bank";
  }
  if (folded === "recurring" || folded.startsWith("recurring-")) return "Recurring";
  // Round 2 (6): the pipeline's own sources read as the drawer already words
  // them ("Statement" / "Balance check" / "Receipt"), not as "Other".
  if (folded === "statement" || folded.startsWith("statement-")) return "Statement";
  if (folded === "assertion" || folded.startsWith("assertion-")) return "Balance check";
  if (folded === "receipt" || folded.startsWith("receipt-")) return "Receipt";
  // rd R8-20b: a source the console does not know is a plain word, never the
  // stored string title-cased into jargon ("hub-gl-push" -> "Hub Gl Push").
  return "Other";
}

/**
 * rd15 A2: the actor a stored source names, in the two cases the console can
 * answer from the entry alone — a rule (the number is in `rule-N`) and the
 * reader's own booking (their login slug is compared here, because the page
 * read the caller's identity).
 *
 * WHY NOT EVERY PERSON. A `queue-approved-<slug>` loses the login's
 * punctuation (`pipeline/sync.py#queue_approved_source`), and no route lists
 * other staff logins, so another person's row is not named here. The Activity
 * drawer does name them: its Worker resolves the slug against `staff_roles`.
 *
 * Lockstep: the slug rule is the pipeline's — lower case, every run of
 * non-alphanumerics becomes one dash, trimmed.
 */
export function bookingSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "");
}

/** The number in a `rule-N` source, when it is exactly that shape. */
export function ruleNumberFromSource(source: string): number | undefined {
  const match = /^rule-([1-9][0-9]*)$/u.exec(source);
  if (match === null) return undefined;
  const value = Number(match[1]);
  return Number.isSafeInteger(value) ? value : undefined;
}

/** The slug a person-authored source carries, or undefined. */
export function personSlugFromSource(source: string): string | undefined {
  const approved = /^(?:queue|moov|hub)-approved-(.+)$/u.exec(source);
  const reclass = /^reclass-(.+)$/u.exec(source);
  const slug = approved?.[1] ?? reclass?.[1];
  return slug === undefined || slug.length === 0 ? undefined : slug;
}

export function provenanceSentence(entry: LedgerEntry): string {
  const folded = entry.source.toLowerCase();
  // rd R8-02: the same fact as the chip, in the drawer's "Where this came
  // from" line. Never "An answer you gave" to someone who did not give it.
  if (folded.startsWith("queue-approved") || folded.startsWith("moov-approved")) {
    return "By a person";
  }
  // Owner grant, 2026-11-02: a transfer between two of the company's own
  // accounts is its own provenance. Saying "Bank line" here would name a feed
  // when the fact that matters is that the money never left the company.
  if (folded === "own-transfer") return "A move between your own accounts";
  if (folded.startsWith("receipt")) return "A receipt";
  if (folded.startsWith("moov")) return "Wallet payment";
  if (folded.startsWith("plaid") || folded.startsWith("bank") || plaidIdOrEmpty(entry.ids) !== "") {
    return "Bank line";
  }
  if (folded.startsWith("rule")) return "A rule";
  if (folded === "recurring" || folded.startsWith("recurring-")) return "A recurring entry";
  return sourceChipLabel(entry.source, entry.ids);
}

/** The two answers above, as one line under the provenance sentence. */
function provenanceDetail(
  entry: LedgerEntry,
  readerSlug: string | null,
): HTMLElement | null {
  const ruleId = ruleNumberFromSource(entry.source);
  if (ruleId !== undefined) {
    const open = h(
      "button",
      { type: "button", class: "btn btn-quiet", "data-action": "open-rule" },
      [`Open rule ${String(ruleId)}`],
    );
    open.addEventListener("click", () => {
      navigateTo("rules", { rule: ruleId });
    });
    return h("p", { "data-role": "books-provenance-detail" }, [
      `Rule ${String(ruleId)} booked it. `,
      open,
    ]);
  }
  const slug = personSlugFromSource(entry.source);
  if (slug !== undefined && readerSlug !== null && slug === readerSlug) {
    return h("p", { "data-role": "books-provenance-detail" }, ["You booked it."]);
  }
  return null;
}

export function humanisedLines(
  entry: LedgerEntry,
  companies: AccountLabelContext,
): string {
  const parts: string[] = [];
  for (const line of entry.view.lines) {
    // The decided word for the processor-fee line ("Processor fee", never the
    // pipeline's "Moov fee"): one wording on screen, per rd-polish-rules §1.
    parts.push(`${lineLabelText(line.label)} ${signedDollars(line.amount)}`);
  }
  const landing = cashLandingPhrase(entry.view.cashAccount, entry.view.cashDelta, companies);
  if (landing !== null) parts.push(landing);
  return parts.join(" \u00b7 ");
}

export function cashLandingPhrase(
  account: AccountPath | undefined,
  delta: string | undefined,
  companies: AccountLabelContext,
): string | null {
  if (delta === undefined || delta === "") return null;
  const negative = amVisibleDisplayText(delta).startsWith("-");
  const magnitude = negative ? signedDollars(delta.slice(1)) : signedDollars(delta);
  if (account !== undefined && isWalletAccount(account)) {
    return negative ? `left the wallet ${magnitude}` : `landed in the wallet ${magnitude}`;
  }
  const where =
    account !== undefined && account !== ""
      ? plainAccountLabel(accountText(account), companies)
      : "cash";
  return negative ? `left ${where} ${magnitude}` : `landed in ${where} ${magnitude}`;
}

/** First moov token, else the plaid id, else the search that found the row. */
export function activitySearchQuery(entry: LedgerEntry, fallback: string): string {
  const moov = entry.ids.moovId?.split(" ")[0] ?? "";
  if (moov !== "") return moov;
  const plaid = plaidIdOrEmpty(entry.ids);
  if (plaid !== "") return plaid;
  const paired = entry.ids.pairedPlaidId?.trim() ?? "";
  if (paired !== "" && paired.toLowerCase() !== "none") return paired;
  return fallback;
}

export function trialSides(closing: string): { debit: string; credit: string } {
  if (closing.startsWith("-")) return { debit: "", credit: closing.slice(1) };
  return { debit: closing, credit: "" };
}

export function debitsEqualCredits(debits: string, credits: string): boolean {
  return debits !== "" && debits === credits;
}

export function bandIsStale(
  state: LedgerHeader["state"],
  period: string,
  asOf: string,
  now: Date,
): boolean {
  if (state !== "open") return false;
  if (period < defaultPeriod(now)) return false;
  const at = Date.parse(asOf);
  if (Number.isNaN(at)) return false;
  return now.getTime() - at > BOOKS_STALE_MS;
}

export function formatBooksStamp(iso: string, now = new Date()): string {
  const phrase = timeAgoText(iso, now);
  return phrase === "" ? "an unknown time" : phrase;
}

export function formatBooksDate(iso: string): string {
  // A date stays a date: the decided `Oct 16, 2026` (rd-polish-rules §1), from
  // the console's one helper, so a journal row can never read as an age.
  return dateText(iso) === "" ? "an unknown date" : dateText(iso);
}

/**
 * The whole-month journal as QuickBooks writes it: one row per posting, with
 * Debit and Credit columns. Text goes through csvField (formula-injection
 * guard); amounts stay plain decimals so a leading minus is never quoted.
 * The export names the month and the entry count is in the page's status line.
 */
export function buildJournalCsv(rows: readonly JournalCsvRow[]): string {
  const headers = [
    "Date",
    "Entry",
    "What",
    "Detail",
    "Source",
    "Account",
    "Debit",
    "Credit",
    "Location",
    "Cash effect",
    "Cash amount",
    "Cash account",
    "Lines",
  ];
  const lines = [headers.join(",")];
  for (const row of rows) {
    const postings =
      row.postings === undefined || row.postings.length === 0
        ? [{ account: "", amount: "", location: "" }]
        : row.postings;
    for (const posting of postings) {
      const sides = posting.amount === "" ? { debit: "", credit: "" } : trialSides(posting.amount);
      lines.push(
        [
          csvField(row.date),
          csvField(row.entryId ?? ""),
          csvField(row.headline),
          csvField(row.subline),
          csvField(row.source),
          csvField(posting.account),
          csvAmount(sides.debit),
          csvAmount(sides.credit),
          csvField(posting.location),
          csvField(row.cashWords),
          csvAmount(row.cashDelta),
          csvField(row.cashAccount),
          csvField(row.lines),
        ].join(","),
      );
    }
  }
  return lines.join("\n");
}

/** Books: journal, accounts and trial balance. Route id stays general-ledger. */
export async function renderGeneralLedgerPage(opts: GeneralLedgerPageOptions): Promise<void> {
  const { client, company, root } = opts;
  // Owner decision 10: the role the shell holds; an unknown role reads as the
  // account manager, never the owner (this page used to assume the owner).
  const role = readerRole(opts.role);
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  const now = opts.now ?? ((): Date => new Date());
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  const params = takeNavParams("general-ledger");
  const requestedView = params?.view;
  let view: BooksView = isBooksView(requestedView) ? requestedView : "journal";
  let period =
    params?.period !== undefined && isValidPeriod(params.period) ? params.period : defaultPeriod(now());
  let q = params?.q ?? "";
  // A ledger path the person clicked or typed; "" is "no filter". Branded so
  // the account face is the one place it becomes words.
  let accountFilter: AccountPath | "" = params?.account ?? "";
  let accountPrefix = params?.accountPrefix ?? "";
  // rd15 A12: a location the report folded to its current spelling. The route
  // folds a stored retired spelling the same way, so the list holds the
  // entries the report counted for that name.
  let locationFilter = params?.location ?? "";
  /**
   * rd15 A10: a report range, so a figure from a quarter or a year opens the
   * months it covers. rd15 A11 adds `accounts`: the counterpart accounts a
   * cash-flow line bucketed, which are a filter, never words on screen.
   */
  let fromFilter = params?.from ?? "";
  let toFilter = params?.to ?? "";
  /**
   * rd15 A11: the accounts a cash-flow line bucketed. They are a filter, never
   * words on screen, and they are removable: picking an account in the control
   * clears them (fix round 1), because a drill that outranks the control leaves
   * the control doing nothing.
   */
  const drillAccounts: AccountPath[] = [...(params?.accounts ?? [])];
  let leaseFilter = "";
  let sourceFilter = params?.source ?? "";
  let searchScope: SearchScope = "month";
  /** Committed month q, kept when the scope is All months. */
  let allQuery = "";
  let allSearchPage = 1;
  let allSearchResult: LedgerSearchResponse | null = null;
  /** Key of the all-months request now running; the same search is not sent twice. */
  let searchInFlightKey: string | null = null;
  let page = 1;
  let loadToken = 0;
  let suppressAutoKey: string | null = null;
  /** The oldest month the books hold; `undefined` until it is read once. */
  let booksStartPeriod: string | null | undefined;
  /**
   * The reader's own login slug, so the drawer can say "You booked it" without
   * a staff list the console has no route for (rd15 A2). Null until read, and
   * null when the read failed: an unknown reader is never guessed at.
   */
  let readerSlug: string | null = null;
  let fillingAccounts = false;
  const accountTypes = new Map<AccountPath, string>();
  /** Each account's balance at the start of the month, for the empty-register sentence. */
  const accountOpenings = new Map<AccountPath, { opening: string; type: string }>();

  const books = h("div", { class: "books", "data-role": "books" });
  root.append(books);

  books.append(
    pageHeader("Books", "Journal, accounts and the trial balance for this company."),
  );

  const segments = segmented(
    "Books section",
    [
      { id: "journal", label: "Journal" },
      { id: "accounts", label: "Accounts" },
      { id: "trial", label: "Trial balance" },
    ],
    view,
    (id) => {
      if (!isBooksView(id) || id === view) return;
      leaveAllSearchMode();
      view = id;
      page = 1;
      markSegments();
      syncFilterVisibility();
      loadInteractive();
    },
  );
  books.append(segments);

  const thisMonthBtn = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-preset": "this-month" },
    ["This month"],
  );
  const lastMonthBtn = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-preset": "last-month" },
    ["Last month"],
  );
  thisMonthBtn.addEventListener("click", () => {
    applyPeriod(defaultPeriod(now()));
  });
  lastMonthBtn.addEventListener("click", () => {
    applyPeriod(shiftPeriod(defaultPeriod(now()), -1));
  });
  const presets = h(
    "div",
    { class: "date-presets", "data-role": "books-presets", role: "group", "aria-label": "Month presets" },
    [thisMonthBtn, lastMonthBtn],
  );

  const periodControl = createPeriodControl({
    period,
    ariaLabel: "Period",
    onChange: (next) => {
      leaveAllSearchMode();
      // Picking a month is a person asking for that month, so a report range
      // is no longer what they want (rd15 A10).
      fromFilter = "";
      toFilter = "";
      period = next;
      page = 1;
      suppressAutoKey = null;
      paintRangeHost();
      loadInteractive();
    },
  });

  const searchInput = h("input", {
    type: "search",
    class: "field-input",
    name: "q",
    value: q,
    placeholder: "Search description, lease, amount or id",
    "aria-label": "Search the journal",
    autocomplete: "off",
    "data-role": "books-search",
  });
  const scopeControl = searchScopeControl("books-search-scope", searchScope, (next) => {
    setSearchScope(next);
  });
  const allSearchButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary books-search-all",
      "data-action": "search-all-months",
      "aria-label": "Search all months",
    },
    ["Search"],
  );
  allSearchButton.hidden = true;
  allSearchButton.addEventListener("click", () => {
    runAllSearch();
  });
  const searchSummary = h("p", {
    class: "books-search-summary",
    "data-role": "search-summary",
    role: "status",
    "aria-live": "polite",
  });
  searchSummary.hidden = true;
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  const applySearch = (): void => {
    if (searchTimer !== undefined) {
      clearTimeout(searchTimer);
      searchTimer = undefined;
    }
    if (searchScope === "all") return;
    const next = searchInput.value;
    if (next.trim() !== q.trim()) suppressAutoKey = null;
    q = next;
    page = 1;
    loadInteractive();
  };
  searchInput.addEventListener("input", () => {
    if (searchScope === "all") {
      if (searchTimer !== undefined) {
        clearTimeout(searchTimer);
        searchTimer = undefined;
      }
      return;
    }
    if (searchTimer !== undefined) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchTimer = undefined;
      applySearch();
    }, SEARCH_DEBOUNCE_MS);
  });
  searchInput.addEventListener("change", () => {
    applySearch();
  });
  searchInput.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (searchScope === "all") runAllSearch();
    else applySearch();
  });

  const accountSelect = h("select", {
    class: "account-filter field-input",
    name: "account",
    "aria-label": "Filter by account",
    "data-role": "books-account-filter",
  });
  accountSelect.append(h("option", { value: "" }, ["All accounts"]));
  accountSelect.addEventListener("change", () => {
    if (fillingAccounts) return;
    // rd15 A11 fix round 1: the account control is the person's own filter, so
    // choosing an account drops the report's set rather than losing to it.
    clearDrillAccounts();
    accountFilter = accountSelect.value;
    page = 1;
    refreshForFilters();
  });

  const locationInput = h("input", {
    type: "text",
    class: "location-filter field-input",
    name: "location",
    value: locationFilter,
    "aria-label": "Filter by location",
    placeholder: "Location",
    autocomplete: "off",
  });
  const leaseInput = h("input", {
    type: "text",
    class: "field-input",
    name: "lease",
    "aria-label": "Filter by lease",
    placeholder: "Lease",
    autocomplete: "off",
    "data-role": "books-lease-filter",
  });
  const sourceInput = h("input", {
    type: "text",
    class: "field-input",
    name: "source",
    value: sourceFilter,
    "aria-label": "Filter by source",
    placeholder: "Source",
    autocomplete: "off",
    "data-role": "books-source-filter",
  });
  bindTextFilter(locationInput, (value) => {
    locationFilter = value;
  });
  bindTextFilter(leaseInput, (value) => {
    leaseFilter = value;
  });
  bindTextFilter(sourceInput, (value) => {
    sourceFilter = value;
  });

  const accountField = fieldWrap("Account", accountSelect);
  const locationField = fieldWrap("Location", locationInput, "Type any part of a location.");
  const leaseField = fieldWrap("Lease", leaseInput, "Type any part of a lease number.");
  const sourceField = fieldWrap("Source", sourceInput, "Type a source, like Bank or Wallet.");
  const exactFields = [accountField, locationField, leaseField, sourceField];
  const searchField = h("div", { class: "books-search-field" }, [
    fieldWrap("Search", searchInput),
    h("div", { class: "books-search-controls" }, [scopeControl, allSearchButton]),
    searchSummary,
  ]);
  const filters = h("div", { class: "books-filters", "data-role": "books-filters" }, [
    searchField,
    ...exactFields,
  ]);

  books.append(h("div", { class: "books-toolbar toolbar" }, [presets, periodControl, filters]));

  // rd15 A10/A12: what a report drill asked for, in words, with one way back.
  // A range replaces the month; a location filter is the same box the person
  // could have typed, prefilled, so the list says why it is short.
  const rangeHost = h("div", { "data-role": "books-range-host" });
  books.append(rangeHost);

  function paintRangeHost(): void {
    clear(rangeHost);
    const lines: HTMLElement[] = [];
    if (rangeActive()) {
      lines.push(
        h("p", { class: "muted", "data-role": "books-range" }, [
          `Showing ${spanWords()}. `,
        ]),
      );
      const clearRange = h(
        "button",
        { type: "button", class: "btn btn-quiet", "data-action": "clear-books-range" },
        ["Show one month"],
      );
      clearRange.addEventListener("click", () => {
        fromFilter = "";
        toFilter = "";
        page = 1;
        paintRangeHost();
        loadInteractive();
      });
      lines[0]?.append(clearRange);
    }
    if (locationFilter !== "") {
      // The report counts income and expenses at a location; the journal holds
      // every entry there, capital ones included. Saying which is which is the
      // difference between a list someone trusts and one that looks wrong.
      lines.push(
        h("p", { class: "muted", "data-role": "books-location-note" }, [
          `Every entry at ${amVisibleDisplayText(locationFilter)}. The By Location report counts only income and expenses there.`,
        ]),
      );
    }
    // rd15 A11 fix round 1: the drilled accounts are a chip, not a silent
    // override. They say which accounts the figure covers, they are removed by
    // one control, and the account filter below clears them by being used.
    // One account is named; several are described, because a cash-flow line
    // can bucket a dozen and a sentence listing them all is not readable.
    if (drillAccounts.length > 0) {
      const name =
        drillAccounts.length === 1
          ? plainAccountLabel(accountText(drillAccounts[0] ?? ""), companies)
          : "every entry on the accounts this figure covers";
      const line = h("p", { class: "muted", "data-role": "books-drill-accounts" }, [
        `Showing ${name}. `,
      ]);
      const clearDrill = h(
        "button",
        {
          type: "button",
          class: "btn btn-quiet",
          "data-action": "clear-books-accounts",
        },
        ["Show all accounts"],
      );
      clearDrill.addEventListener("click", () => {
        clearDrillAccounts();
        accountFilter = "";
        accountSelect.value = "";
        page = 1;
        loadInteractive();
      });
      line.append(clearDrill);
      lines.push(line);
    }
    for (const line of lines) rangeHost.append(line);
  }
  paintRangeHost();

  /** Drop the report's set of accounts; the account control owns the filter again. */
  function clearDrillAccounts(): void {
    if (drillAccounts.length === 0) return;
    drillAccounts.splice(0, drillAccounts.length);
    paintRangeHost();
  }

  const bandHost = h("div", { "data-role": "books-band-host" });
  const alertHost = h("div", { "data-role": "books-alert-host" });
  const refreshCopy = h("p", { class: "books-refresh-copy", "data-role": "books-refresh-copy" }, [
    "The Books view refreshes after each booking run and about every hour. New bank and wallet activity is booked at the next booking run (up to 8 hours).",
  ]);
  const body = h("div", { "data-role": "books-body" });
  const pager = h("div", { class: "pager", "data-role": "gl-pager" });
  books.append(bandHost, alertHost, refreshCopy, body, pager);

  function bindTextFilter(input: HTMLInputElement, assign: (value: string) => void): void {
    const apply = (): void => {
      assign(input.value.trim());
      page = 1;
      refreshForFilters();
    };
    input.addEventListener("change", apply);
    input.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      apply();
    });
  }

  function fieldWrap(label: string, control: HTMLElement, hint?: string): HTMLElement {
    return h("label", { class: "books-field" }, [
      h("span", { class: "books-field-label" }, [label]),
      control,
      hint === undefined
        ? null
        : h("span", { class: "books-field-hint" }, [hint]),
    ]);
  }

  function markSegments(): void {
    const buttons = segments.querySelectorAll<HTMLButtonElement>("button[data-segment]");
    for (const btn of buttons) {
      const on = btn.getAttribute("data-segment") === view;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    }
  }

  function syncFilterVisibility(): void {
    const show = view === "journal";
    filters.hidden = !show;
    if (show) filters.removeAttribute("hidden");
    else filters.setAttribute("hidden", "true");
  }

  function syncPresets(): void {
    const current = defaultPeriod(now());
    thisMonthBtn.classList.toggle("active", period === current);
    lastMonthBtn.classList.toggle("active", period === shiftPeriod(current, -1));
  }

  function applyPeriod(next: string): void {
    leaveAllSearchMode();
    fromFilter = "";
    toFilter = "";
    paintRangeHost();
    const input = periodControl.querySelector("input");
    if (!(input instanceof HTMLInputElement)) return;
    if (input.value === next) {
      period = next;
      page = 1;
      loadInteractive();
      return;
    }
    input.value = next;
    input.dispatchEvent(new Event("change"));
  }

  function currentQuery(): LedgerQuery {
    // The month (or the range) comes first: the object's key order is part of
    // what the request-budget harness prints, and it pins the month shape.
    const query: LedgerQuery = {};
    // A report range spans months, so the month is not named; the route reads
    // the months the range covers (rd15 A10).
    if (rangeActive()) {
      query.from = fromFilter;
      query.to = toFilter;
    } else {
      query.period = period;
    }
    query.page = page;
    query.pageSize = JOURNAL_PAGE_SIZE;
    const search = q.trim();
    if (search !== "") query.q = search;
    if (drillAccounts.length > 0) query.accounts = drillAccounts;
    else if (accountFilter !== "") query.account = accountFilter;
    if (accountPrefix !== "") query.accountPrefix = accountPrefix;
    if (locationFilter !== "") query.location = locationFilter;
    if (leaseFilter !== "") query.lease = leaseFilter;
    if (sourceFilter !== "") query.source = sourceFilter;
    return query;
  }

  function refillAccountSelect(): void {
    fillingAccounts = true;
    const previous = accountFilter;
    clear(accountSelect);
    accountSelect.append(h("option", { value: "" }, ["All accounts"]));
    const accounts = [...accountTypes.keys()];
    const labels = disambiguatedAccountLabels(accounts, companies);
    const seen = new Set<AccountPath>();
    for (const account of accounts) {
      seen.add(account);
      // The label is decided before it reaches the control: the path itself is
      // the option's value (the wire), never its words.
      const label = labels.get(account) ?? plainAccountLabel(accountText(account), companies);
      accountSelect.append(h("option", { value: account }, [label]));
    }
    if (previous !== "" && !seen.has(previous)) {
      const withPrevious = disambiguatedAccountLabels([...accounts, previous], companies);
      const leftover =
        withPrevious.get(previous) ?? plainAccountLabel(accountText(previous), companies);
      accountSelect.append(h("option", { value: previous }, [leftover]));
    }
    accountSelect.value = previous;
    fillingAccounts = false;
  }

  function rememberAccounts(accounts: readonly LedgerAccount[]): void {
    accountTypes.clear();
    accountOpenings.clear();
    for (const account of accounts) {
      accountTypes.set(account.account, account.type);
      accountOpenings.set(account.account, { opening: account.opening, type: account.type });
    }
    refillAccountSelect();
  }

  function goToAccount(account: AccountPath): void {
    if (searchTimer !== undefined) {
      clearTimeout(searchTimer);
      searchTimer = undefined;
    }
    leaveAllSearchMode();
    clearDrillAccounts();
    accountFilter = account;
    accountPrefix = "";
    q = "";
    locationFilter = "";
    leaseFilter = "";
    sourceFilter = "";
    searchInput.value = "";
    locationInput.value = "";
    leaseInput.value = "";
    sourceInput.value = "";
    page = 1;
    view = "journal";
    suppressAutoKey = null;
    markSegments();
    syncFilterVisibility();
    refillAccountSelect();
    accountSelect.value = account;
    loadInteractive();
  }

  /**
   * One pending-queue read per load. The band's count and the not-loaded
   * view's button read it once, so the page makes one read and states one
   * number (item B4b-1). A failed read is not a count: the band says nothing.
   */
  let pendingRead: {
    token: number;
    promise: Promise<{ items: readonly QueueItem[]; complete: boolean } | null>;
  } | null = null;

  function readPendingCards(
    token: number,
  ): Promise<{ items: readonly QueueItem[]; complete: boolean } | null> {
    if (pendingRead !== null && pendingRead.token === token) return pendingRead.promise;
    const promise = client
      .listQueue({ company, status: "pending" })
      .then((listed) => ({
        items: listed.items,
        // The Worker caps this list; at the cap the read is not complete.
        complete: !fleetQueueListCapFired(listed.items.length),
      }))
      .catch(() => null);
    pendingRead = { token, promise };
    return promise;
  }

  /**
   * How many cards wait for THIS reader, in the same words To review counts.
   *
   * The role-free header count included the owner's escalated cards, so Books
   * said 23 where To review said 22 for an account manager (round-11 item 11 /
   * rc-4 C1, C2). `reviewCards` decides the number once; `countText` says so
   * when the read was capped.
   */
  async function paintWaitingForReview(band: HTMLElement): Promise<void> {
    const token = loadToken;
    const read = await readPendingCards(token);
    if (read === null || token !== loadToken || !stillCurrent()) return;
    const cards = reviewCards(measuredSetOf(read.items, read.complete), role, company);
    const n = cards.openable.length;
    if (n <= 0) return;
    const review = h(
      "button",
      { type: "button", class: "btn btn-quiet", "data-action": "open-review" },
      [navLabel("queue")],
    );
    review.addEventListener("click", () => {
      navigateTo("queue");
    });
    // The sentence names the scope (every month) and never one month; a capped
    // read reads "500+" rather than a number it cannot support.
    const one = n === 1 && read.complete;
    band.append(
      h("p", { "data-role": "books-waiting" }, [
        `${countText(n, read.complete)} ${one ? "card is" : "cards are"} still waiting for review across all months, and ${one ? "is" : "are"} not in the books yet `,
        review,
      ]),
    );
  }

  function paintHeader(header: LedgerHeader): void {
    clear(bandHost);
    clear(alertHost);
    const stale = bandIsStale(header.state, header.period, header.asOf, now());
    const bandClass =
      header.state === "closed"
        ? "books-band books-band--closed"
        : stale
          ? "books-band books-band--stale"
          : "books-band";
    const stateLine =
      header.state === "closed"
        ? "Closed"
        : `Not closed or reconciled \u00b7 books as of ${formatBooksStamp(header.asOf, now())}`;
    const band = h("div", {
      class: bandClass,
      "data-role": "books-band",
      "data-state": header.state,
      "data-stale": stale ? "true" : "false",
    });
    band.append(h("p", { class: "books-band-state", "data-role": "books-band-state" }, [stateLine]));
    if (stale) {
      band.append(
        h("p", { class: "books-band-stale", "data-role": "books-stale" }, [
          // Round 2 (5): an age ("5 minutes ago"), never a bare UTC date.
          `Last updated ${formatBooksStamp(header.asOf, now())}`,
        ]),
      );
    }
    band.append(
      h("p", { class: "books-band-loaded", "data-role": "books-loaded" }, [
        `Books view last loaded ${formatBooksStamp(header.ledgerCommitAt, now())}`,
      ]),
    );
    bandHost.append(band);
    // The count is painted from the queue read below, through `reviewCards`, so
    // Books and To review state one number for one reader (item B4b-1).
    void paintWaitingForReview(band);
    if (header.loaderErrors > 0) {
      // The owner can open Connection status; the account manager cannot, so
      // the account manager is offered the press it can make (Check now) by
      // `advice`, never a page it cannot reach.
      alertHost.append(
        banner(
          "warn",
          `The ledger has an error, so some numbers may be paused. ${
            role === "owner" ? `Check ${navLabel("health")}.` : advice("check_now", role)
          }`,
        ),
      );
    }
  }

  function presentEntry(entry: LedgerEntry): ShownEntry {
    const shown: ShownEntry = {
      entry,
      entryId: entry.id,
      date: amVisibleDisplayText(entry.date),
      headline: amVisibleDisplayText(entry.view.headline),
      subline: amVisibleDisplayText(entry.view.subline ?? ""),
      cashWords: cashEffectWords(entry.view.cashDelta),
      cashDelta: entry.view.cashDelta ?? "",
      cashAccount:
        entry.view.cashAccount !== undefined && entry.view.cashAccount !== ""
          ? plainAccountLabel(accountText(entry.view.cashAccount), companies)
          : "",
      source: sourceChipLabel(entry.source, entry.ids),
      lines: humanisedLines(entry, companies),
      postings: entry.postings.map((posting) => ({
        account: posting.account,
        amount: posting.amount,
        location: posting.location ?? "",
      })),
    };
    if (accountFilter !== "") {
      shown.accountAmount = entry.accountAmount ?? "";
      shown.balanceAfter = entry.balanceAfter ?? "";
    }
    return shown;
  }

  function postingsBlock(entry: LedgerEntry): HTMLElement {
    const postingAccounts = entry.postings.map((posting) => posting.account);
    const labels = disambiguatedAccountLabels(postingAccounts, companies);
    const table = h("table", { class: "data-table books-postings", "aria-label": "Postings" });
    table.append(
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Account"]),
          h("th", {}, ["Location"]),
          h("th", { class: "num" }, ["Debit"]),
          h("th", { class: "num" }, ["Credit"]),
        ]),
      ]),
    );
    const tbody = h("tbody", {});
    let debits = 0n;
    let credits = 0n;
    for (const posting of entry.postings) {
      const location = posting.location === undefined || posting.location === ""
        ? "\u2014"
        : amVisibleDisplayText(posting.location);
      const sides = trialSides(posting.amount);
      debits += centsOf(sides.debit);
      credits += centsOf(sides.credit);
      const postingLabel =
        labels.get(posting.account) ?? plainAccountLabel(accountText(posting.account), companies);
      tbody.append(
        h("tr", { "data-role": "books-posting", "data-amount": posting.amount, "data-account": posting.account }, [
          h("td", {}, [postingLabel]),
          h("td", {}, [location]),
          h("td", { class: "num", "data-role": "books-posting-debit", "data-amount": sides.debit }, [
            sides.debit === "" ? "" : formatLedgerMoney(sides.debit),
          ]),
          h("td", { class: "num", "data-role": "books-posting-credit", "data-amount": sides.credit }, [
            sides.credit === "" ? "" : formatLedgerMoney(sides.credit),
          ]),
        ]),
      );
    }
    tbody.append(
      h("tr", { class: "books-posting-totals", "data-role": "books-posting-totals" }, [
        h("th", { scope: "row", colspan: "2" }, ["Total"]),
        h("td", { class: "num", "data-role": "books-posting-debit-total", "data-amount": centsToDecimal(debits) }, [
          formatLedgerMoney(centsToDecimal(debits)),
        ]),
        h("td", { class: "num", "data-role": "books-posting-credit-total", "data-amount": centsToDecimal(credits) }, [
          formatLedgerMoney(centsToDecimal(credits)),
        ]),
      ]),
    );
    table.append(tbody);
    return h("div", { class: "books-posting-block" }, [
      h("div", { class: "table-scroll" }, [table]),
      h("p", { class: "books-humanised", "data-role": "books-humanised" }, [humanisedLines(entry, companies)]),
    ]);
  }

  function paintJournal(glPage: LedgerEntriesPage): void {
    clear(body);
    const rows = glPage.entries.map(presentEntry);
    const download = h("button", {
      type: "button",
      class: "btn btn-secondary",
      "data-role": "books-csv",
      "aria-label": "Download the whole month as CSV",
    }, ["Download CSV"]);
    const exportStatus = h("p", {
      class: "books-export-status",
      "data-role": "books-csv-progress",
      role: "status",
      "aria-live": "polite",
      hidden: "true",
    });
    download.addEventListener("click", () => {
      void downloadWholeMonth(download, exportStatus);
    });

    const tools = h("div", { class: "books-journal-tools" }, [download, exportStatus]);
    body.append(tools);

    if (glPage.entries.length === 0) {
      const filtered = filtersActive();
      // An empty month is an answer, not a failure: no Try again button here.
      // A failed read has its own state with the button (classifyBooksFailure).
      body.append(
        h("div", { class: "empty-state", "data-role": "gl-empty" }, [
          h("p", {}, [emptyJournalSentence(filtered)]),
        ]),
      );
    } else {
      const list = h("div", {
        class: "books-journal",
        role: "list",
        "aria-label": "Journal",
        "data-role": "books-journal",
      });
      for (const row of rows) list.append(journalArticle(row));
      body.append(list);
    }
    paintPager(glPage);
    maybeAutoOpen(glPage, rows);
  }

  function showExportStatus(host: HTMLElement, text: string): void {
    clear(host);
    host.hidden = false;
    host.removeAttribute("hidden");
    host.append(text);
  }

  async function downloadWholeMonth(
    button: HTMLButtonElement,
    status: HTMLElement,
  ): Promise<void> {
    if (button.disabled) return;
    button.disabled = true;
    try {
      const collected: LedgerEntry[] = [];
      let nextPage = 1;
      let total = 0;
      for (;;) {
        showExportStatus(
          status,
          `Preparing CSV… ${String(collected.length)} of ${String(total)} entries`,
        );
        const query = { ...currentQuery(), page: nextPage, pageSize: EXPORT_PAGE_SIZE };
        const result = await client.getLedger(company, query);
        total = result.total;
        if (total > EXPORT_MAX_ENTRIES) {
          // One sentence for both roles: the ceiling is the page's own, so
          // there is nothing an owner could do about it either.
          showExportStatus(
            status,
            `This month has more than ${String(EXPORT_MAX_ENTRIES)} entries, so it cannot be downloaded at once.`,
          );
          return;
        }
        collected.push(...result.entries);
        showExportStatus(
          status,
          `Preparing CSV… ${String(collected.length)} of ${String(total)} entries`,
        );
        if (!result.hasMore || result.entries.length === 0) break;
        nextPage += 1;
        if (nextPage > Math.ceil(total / Math.max(1, result.pageSize)) + 1) {
          showExportStatus(status, "The download stopped early. Try again.");
          return;
        }
      }
      const rows = collected.map(presentEntry);
      const csv = buildJournalCsv(rows);
      // The file name carries the slug as a key, through the one key helper.
      const fileSpan = rangeActive()
        ? `${fromFilter}-to-${toFilter}`
        : period;
      saveCsv(`books-${slugText(company)}-${fileSpan}.csv`, csv);
      showExportStatus(
        status,
        `Downloaded ${pluralText(collected.length, "entry", "entries")} for ${spanWords()}.`,
      );
    } catch {
      showExportStatus(status, "The CSV could not be prepared. Try again.");
    } finally {
      button.disabled = false;
    }
  }

  /**
   * Why the list is empty, in the words that are true. Clicking an account
   * sets one filter the person did not type; when that account already had a
   * balance at the start of the month, its entries are in an earlier month and
   * the page says so instead of blaming "these filters" (integration review N6).
   */
  function emptyJournalSentence(filtered: boolean): string {
    if (rangeActive()) {
      return filtered
        ? "No entries match these filters in this range. Clear a filter or try a wider range."
        : "No entries in this range. New activity appears after the next booking run.";
    }
    if (!filtered) return "No entries in this month. New activity appears after the next booking run.";
    const onlyAccount =
      accountFilter !== "" &&
      q.trim() === "" &&
      accountPrefix === "" &&
      locationFilter === "" &&
      leaseFilter === "" &&
      sourceFilter === "";
    if (!onlyAccount) return "No entries match these filters. Clear a filter or try again.";
    const label = plainAccountLabel(accountText(accountFilter), companies);
    const month = formatPeriodLabel(period);
    const known = accountOpenings.get(accountFilter);
    const started = known === undefined ? null : decimalToCents(known.opening);
    if (known !== undefined && started !== null && started !== 0n) {
      const shown = formatLedgerMoney(shownBalance(known.opening, known.type));
      return `${label} has no entries in ${month}. Its ${shown} was already in the books when the month began, so look in an earlier month.`;
    }
    return `${label} has no entries in ${month}. Clear the account filter or try another month.`;
  }

  function filtersActive(): boolean {
    return (
      q.trim() !== "" ||
      accountFilter !== "" ||
      accountPrefix !== "" ||
      locationFilter !== "" ||
      leaseFilter !== "" ||
      sourceFilter !== "" ||
      drillAccounts.length > 0
    );
  }

  /** A report range is being read instead of one month (rd15 A10). */
  function rangeActive(): boolean {
    return fromFilter !== "" && toFilter !== "";
  }

  /** The month the range ends in: what the accounts read answers for. */
  function anchorPeriod(): string {
    if (!rangeActive()) return period;
    const month = toFilter.slice(0, 7);
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : period;
  }

  /**
   * The words for what the page is reading: the month, or the report's range.
   * One place, so the band, the empty sentence and the file name agree.
   */
  function spanWords(): string {
    if (!rangeActive()) return formatPeriodLabel(period);
    const from = dateText(fromFilter) || fromFilter;
    const to = dateText(toFilter) || toFilter;
    return `${from} to ${to}`;
  }

  function journalArticle(row: ShownEntry): HTMLElement {
    const open = h(
      "button",
      {
        type: "button",
        class: "books-entry-main",
        "data-action": "open-entry",
        "aria-label": `Open ${row.headline}`,
      },
      [
        h("span", { class: "books-date", "data-role": "books-date" }, [dateText(row.date) || row.date]),
        h("span", { class: "books-entry-what" }, [
          h("span", { class: "books-headline", "data-role": "books-headline" }, [row.headline]),
          row.subline === ""
            ? null
            : h("span", { class: "books-subline", "data-role": "books-subline" }, [row.subline]),
        ]),
        h(
          "span",
          {
            class: row.cashDelta.startsWith("-") ? "books-cash books-cash--out" : "books-cash books-cash--in",
            "data-role": "books-cash",
            "data-amount": row.cashDelta,
          },
          [
            h("span", { "data-role": "books-cash-words" }, [row.cashWords === "" ? "\u2014" : row.cashWords]),
            row.cashAccount === ""
              ? null
              : h("span", { class: "books-cash-account", "data-role": "books-cash-account" }, [row.cashAccount]),
          ],
        ),
        h("span", { class: "chip chip-info", "data-role": "books-source" }, [row.source]),
      ],
    );
    if (accountFilter !== "") {
      open.append(accountFigures(row));
    }
    open.addEventListener("click", () => {
      openDrawer(row);
    });
    const article = h(
      "article",
      {
        class: "books-entry",
        role: "listitem",
        "data-role": "books-entry",
        "data-entry-id": row.entry.id,
      },
      [open, disclosure("Postings", postingsBlock(row.entry))],
    );
    return article;
  }

  function accountFigures(row: ShownEntry): HTMLElement {
    const amount = row.accountAmount ?? "";
    const balance = row.balanceAfter ?? "";
    // rd Y8 grant (owner, 2026-10-27): the register Balance gets the same
    // display-only flip the Accounts tab uses, chosen by the filtered account's
    // type, so Rent reads $600.00 in both places. No type (a register that is
    // not filtered to one account) leaves the figure exactly as it was, and the
    // data attribute keeps the ledger's own signed balance.
    const accountType = accountTypes.get(accountFilter) ?? "";
    const shownBalanceAfter = shownBalance(balance, accountType);
    // The Amount of one entry takes the same flip (integration review N6): a
    // rent payment read "Amount −$500.00 / Balance $600.00" on Income. Display
    // only, by the same account type; expenses and assets are unchanged.
    const shownAmount = shownBalance(amount, accountType);
    return h("span", { class: "books-account-figures" }, [
      figure("Amount", "books-account-amount", amount, shownAmount),
      figure("Balance", "books-balance-after", balance, shownBalanceAfter),
    ]);
  }

  function figure(label: string, role: string, amount: string, shown = amount): HTMLElement {
    return h("span", { class: "books-figure" }, [
      h("span", { class: "books-figure-label" }, [label]),
      h(
        "span",
        { class: "num", "data-role": role, "data-amount": amount },
        [shown === "" ? "\u2014" : formatLedgerMoney(shown)],
      ),
    ]);
  }

  function paintPager(glPage: LedgerEntriesPage): void {
    clear(pager);
    const size = glPage.pageSize > 0 ? glPage.pageSize : JOURNAL_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(glPage.total / size));
    const prev = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "gl-prev", "aria-label": "Previous page" },
      ["\u2039 Prev"],
    );
    const next = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "gl-next", "aria-label": "Next page" },
      ["Next \u203a"],
    );
    prev.disabled = glPage.page <= 1;
    next.disabled = !glPage.hasMore;
    prev.addEventListener("click", () => {
      page = Math.max(1, page - 1);
      loadInteractive();
    });
    next.addEventListener("click", () => {
      if (!glPage.hasMore) return;
      page += 1;
      loadInteractive();
    });
    pager.append(
      prev,
      h("span", { class: "pager-info", "data-role": "gl-page-info" }, [
        `Page ${String(glPage.page)} of ${String(totalPages)} \u00b7 ${pluralText(glPage.total, "entry", "entries")}`,
      ]),
      next,
    );
  }

  function maybeAutoOpen(glPage: LedgerEntriesPage, rows: readonly ShownEntry[]): void {
    if (q.trim() === "" || glPage.total !== 1 || rows.length !== 1) return;
    const row = rows[0];
    if (row === undefined) return;
    const key = `${period}|${q.trim()}|${row.entry.id}`;
    if (suppressAutoKey === key) return;
    openDrawer(row);
  }

  function openDrawer(row: ShownEntry): void {
    closeDrawer();
    const entry = row.entry;
    const tech = h("dl", { "data-role": "technical-details" });
    addDetail(tech, "Plaid", plaidIdOrEmpty(entry.ids));
    addDetail(tech, "Moov", entry.ids.moovId);
    addDetail(tech, "Paired bank line", entry.ids.pairedPlaidId);
    addDetail(tech, "Lease", entry.ids.lease);
    addDetail(tech, "Correction of", entry.ids.correctionOf);
    addDetail(tech, "Correction kind", entry.ids.correctionKind);
    if (tech.childElementCount === 0) {
      tech.append(h("p", {}, ["No further details."]));
    }
    const see = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "see-in-activity" },
      ["See in Activity"],
    );
    see.addEventListener("click", () => {
      const periodParam = entry.date.slice(0, 7);
      const query: { q?: string; period?: string } = {};
      const found = activitySearchQuery(entry, q.trim());
      if (found !== "") query.q = found;
      if (isValidPeriod(periodParam)) query.period = periodParam;
      navigateTo("transactions", query);
    });
    const panel = h("div", { class: "books-drawer-body" }, [
      h("p", { class: "books-drawer-date" }, [dateText(row.date) || row.date]),
      row.subline === "" ? null : h("p", { "data-role": "books-subline" }, [row.subline]),
      h("p", { "data-role": "books-cash-words" }, [
        row.cashWords === "" ? "\u2014" : row.cashWords,
        row.cashAccount === "" ? "" : ` \u00b7 ${row.cashAccount}`,
      ]),
      h("p", {}, [h("span", { class: "chip chip-info", "data-role": "books-source" }, [row.source])]),
      entry.payee === undefined || entry.payee === ""
        ? null
        : h("p", { "data-role": "books-payee" }, ["Payee ", amVisibleDisplayText(entry.payee)]),
      postingsBlock(entry),
      h("h3", {}, ["Where this came from"]),
      h("p", { "data-role": "books-provenance" }, [provenanceSentence(entry)]),
      provenanceDetail(entry, readerSlug),
      disclosure("Show details", tech),
      see,
    ]);
    const sheet = drawer(row.headline, panel, () => {
      suppressAutoKey = `${period}|${q.trim()}|${entry.id}`;
      closeDrawer();
    });
    books.append(sheet);
  }

  function closeDrawer(): void {
    books.querySelector('[data-role="drawer"]')?.remove();
  }

  function paintAccounts(response: LedgerAccountsResponse): void {
    clear(body);
    clear(pager);
    const grouped = new Map<AccountGroup, LedgerAccount[]>();
    for (const account of response.accounts) {
      const group = accountGroup(account.type);
      const list = grouped.get(group) ?? [];
      list.push(account);
      grouped.set(group, list);
    }
    const wrap = h("div", { "data-role": "books-accounts" });
    if (response.accounts.length === 0) {
      wrap.append(
        h("p", { class: "empty", "data-role": "books-accounts-empty" }, ["No accounts in this month."]),
      );
    }
    for (const name of GROUP_ORDER) {
      const rows = grouped.get(name);
      if (rows === undefined || rows.length === 0) continue;
      wrap.append(h("h2", { class: "books-group-title" }, [name]));
      wrap.append(accountsTable(name, rows));
    }
    body.append(wrap);
  }

  function accountsTable(group: AccountGroup, rows: readonly LedgerAccount[]): HTMLElement {
    const activity = showsActivityColumn(group);
    const offered = rows.map((account) => account.account);
    const labels = disambiguatedAccountLabels(offered, companies);
    // The column says which way the group counts, because Income, Liabilities
    // and Equity show a credit balance as a positive number.
    const side = countsAsSide(group);
    const balanceHead = activity ? "Balance since books started" : "Balance";
    const head = h("tr", {}, [
      h("th", {}, ["Account"]),
      activity ? h("th", { class: "num" }, ["This month"]) : null,
      h(
        "th",
        { class: "num", "data-role": "books-balance-head", "data-side": side ?? "" },
        [side === null ? balanceHead : `${balanceHead} (${side})`],
      ),
    ]);
    const tbody = h("tbody", {});
    for (const account of rows) {
      const highlight = accountFilter === account.account;
      const accountWords =
        labels.get(account.account) ?? plainAccountLabel(accountText(account.account), companies);
      const open = h(
        "button",
        {
          type: "button",
          class: "books-account-link",
          "data-action": "open-account",
          "data-account": account.account,
        },
        [accountWords],
      );
      open.addEventListener("click", () => {
        goToAccount(account.account);
      });
      const tr = h(
        "tr",
        {
          "data-role": "books-account-row",
          "data-account": account.account,
          "data-group": group,
          "data-closing": account.closing,
          "data-activity": account.activity,
          class: highlight ? "books-row-highlight" : "",
          "data-highlight": highlight ? "true" : "false",
        },
        [
          h("td", {}, [open]),
          activity
            ? h("td", { class: "num", "data-role": "books-this-month", "data-amount": account.activity }, [
                formatLedgerMoney(shownBalance(account.activity, account.type)),
              ])
            : null,
          h("td", { class: "num", "data-role": "books-closing", "data-amount": account.closing }, [
            formatLedgerMoney(shownBalance(account.closing, account.type)),
          ]),
        ],
      );
      tbody.append(tr);
    }
    const table = h("table", { class: "data-table", "aria-label": `${group} accounts` }, [
      h("thead", {}, [head]),
      tbody,
    ]);
    return h("div", { class: "table-scroll", "data-role": "books-account-group", "data-group": group }, [table]);
  }

  function paintTrial(response: LedgerAccountsResponse): void {
    clear(body);
    clear(pager);
    const trialAccounts = response.accounts.map((account) => account.account);
    const labels = disambiguatedAccountLabels(trialAccounts, companies);
    const tbody = h("tbody", {});
    for (const account of response.accounts) {
      const sides = trialSides(account.closing);
      const trialLabel =
        labels.get(account.account) ?? plainAccountLabel(accountText(account.account), companies);
      tbody.append(
        h("tr", {
          "data-role": "books-trial-row",
          "data-account": account.account,
          "data-closing": account.closing,
        }, [
          h("td", {}, [trialLabel]),
          moneyCell("books-debit", sides.debit),
          moneyCell("books-credit", sides.credit),
        ]),
      );
    }
    tbody.append(
      h("tr", { "data-role": "books-trial-totals" }, [
        h("th", { scope: "row" }, ["Total"]),
        moneyCell("books-trial-debit-total", response.trialBalance.totalDebits),
        moneyCell("books-trial-credit-total", response.trialBalance.totalCredits),
      ]),
    );
    const table = h("table", { class: "data-table", "aria-label": "Trial balance" }, [
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Account"]),
          h("th", { class: "num" }, ["Debit"]),
          h("th", { class: "num" }, ["Credit"]),
        ]),
      ]),
      tbody,
    ]);
    const equal = debitsEqualCredits(
      response.trialBalance.totalDebits,
      response.trialBalance.totalCredits,
    );
    const status = h("p", {
      class: equal ? "books-trial-status books-trial-status--ok" : "books-trial-status books-trial-status--bad",
      "data-role": "books-trial-status",
      "data-balanced": equal ? "true" : "false",
    });
    if (equal) {
      status.append(
        h("span", { class: "books-check", "data-role": "books-trial-check", "aria-hidden": "true" }, [
          svgIcon("check"),
        ]),
      );
      status.append(document.createTextNode(" Debits equal credits"));
    } else {
      status.append(
        document.createTextNode(
          "These do not balance. This is a problem in the ledger, not in this screen.",
        ),
      );
    }
    body.append(h("div", { class: "table-scroll", "data-role": "books-trial" }, [table]), status);
  }

  function moneyCell(role: string, amount: string): HTMLElement {
    return h(
      "td",
      { class: "num", "data-role": role, "data-amount": amount },
      [amount === "" ? "" : formatLedgerMoney(amount)],
    );
  }

  function paintFailure(error: unknown, token: number): Promise<void> {
    const failure = classifyBooksFailure(error, role);
    if (failure.kind === "not-published") {
      return paintNotPublished(token);
    }
    if (failure.kind === "not-enabled") {
      // Role rule (rd-polish-rules §1) and decision 2: no console control
      // switches the books view on, for either role, so both read the state and
      // the decided next step instead of being sent to the owner.
      body.append(
        h("div", { class: "books-unpublished", "data-role": "books-not-enabled", role: "status" }, [
          h("p", {}, [
            `${BOOKS_VIEW_OFF_TEXT} ${advice("switch_on_books_view", role)}`,
          ]),
        ]),
      );
      return Promise.resolve();
    }
    if (failure.kind === "statements") {
      // A retry is the reader's, for both roles: the account manager was told
      // "Ask the owner, then try again" with no action of their own named.
      body.append(
        h("div", { class: "books-unpublished", "data-role": "books-statements-unavailable", role: "status" }, [
          h("p", {}, ["This month's books could not be shown. Try again."]),
          tryAgainButton(() => {
            loadInteractive();
          }),
        ]),
      );
      return Promise.resolve();
    }
    const state = errorState(failure.sentence, () => {
      loadInteractive();
    });
    relabelTryAgain(state);
    body.append(h("div", { "data-role": "gl-error" }, [state]));
    return Promise.resolve();
  }

  /**
   * A month the books view does not hold is not a thing to wait for. The
   * coverage (the oldest month the books hold) is read once per page, from the
   * accounts route, so an old month can say plainly that it will never load
   * instead of offering Check now forever.
   */
  function paintNotPublished(token: number): Promise<void> {
    return readBooksStart().then((first) => {
      if (token !== loadToken || !stillCurrent()) return;
      const month = monthText(period) || formatPeriodLabel(period);
      if (first !== null && period < first) {
        body.append(
          h("div", { class: "books-unpublished", "data-role": "books-not-published", role: "status" }, [
            h("p", { class: "books-empty-title" }, [`The books do not have ${month} yet.`]),
            h("p", {}, [`They start in ${monthText(first)}.`]),
          ]),
        );
        return;
      }
      const checkHost = h("div", { class: "books-check-host", "data-role": "books-check-host" });
      renderCheckNow({ client, company, root: checkHost, stillCurrent });
      const check = h(
        "button",
        { type: "button", class: "btn btn-secondary", "data-action": "check-for-updates" },
        ["Check for updates"],
      );
      check.addEventListener("click", () => {
        const target = checkHost.querySelector<HTMLButtonElement>('[data-action="check-now"]');
        target?.click();
      });
      const waitingHost = h("div", { "data-role": "books-answer-waiting" });
      body.append(
        h("div", { class: "books-unpublished", "data-role": "books-not-published", role: "status" }, [
          h("p", { class: "books-empty-title" }, [MONTH_NOT_LOADED_TEXT]),
          h("p", {}, [
            "This page shows entries once they are saved to the books. It refreshes after each booking run and about every hour.",
          ]),
          check,
          checkHost,
          waitingHost,
        ]),
      );
      void countWaiting(token, waitingHost);
    });
  }

  /** The oldest month the books hold; null when it cannot be learned. */
  async function readBooksStart(): Promise<string | null> {
    if (booksStartPeriod !== undefined) return booksStartPeriod;
    try {
      const page = await client.getLedgerAccounts(company, defaultPeriod(now()));
      let first: string | null = null;
      for (const row of page.header.periods) {
        if (first === null || row.period < first) first = row.period;
      }
      booksStartPeriod = first;
    } catch {
      // Not knowing the coverage is not a failure state: the month keeps the
      // sentence and the button it had before.
      booksStartPeriod = null;
    }
    return booksStartPeriod;
  }

  async function countWaiting(token: number, host: HTMLElement): Promise<void> {
    const read = await readPendingCards(token);
    if (read === null) return; // The count is optional; the explanation stands.
    if (token !== loadToken || !stillCurrent()) return;
    // The same derivation as the band: one number, for this reader.
    const cards = reviewCards(measuredSetOf(read.items, read.complete), role, company);
    const n = cards.openable.length;
    if (n <= 0) return;
    // The Worker caps this list; at the cap the button says "or more".
    const answer = h(
      "button",
      { type: "button", class: "btn btn-quiet", "data-action": "answer-waiting" },
      [
        `Answer ${countedText(n, read.complete, "waiting question", "waiting questions")}`,
      ],
    );
    answer.addEventListener("click", () => {
      navigateTo("queue");
    });
    clear(host);
    host.append(answer);
  }

  async function loadInner(): Promise<void> {
    if (searchScope !== "month") return;
    const token = ++loadToken;
    hideSearchSummary();
    setSearchResultsMode(false);
    closeDrawer();
    clear(body);
    clear(pager);
    clear(alertHost);
    syncPresets();
    syncFilterVisibility();
    body.append(h("p", { class: "muted", "data-role": "gl-loading" }, ["Loading books\u2026"]));
    try {
      if (view === "journal") {
        const query = currentQuery();
        if (!ledgerAccountFilterFits(query)) {
          // rd15 A11 fix round 2: a list the edge would refuse is not sent, so
          // there is no 400 to render. The drill chip above keeps the way out.
          clear(body);
          clear(pager);
          closeDrawer();
          body.append(
            h(
              "p",
              { class: "muted", role: "status", "data-role": "books-accounts-too-many" },
              [TOO_MANY_ACCOUNTS_TEXT],
            ),
          );
          return;
        }
        const ledgerPromise = client.getLedger(company, query);
        const accountsPromise = client.getLedgerAccounts(company, anchorPeriod()).then(
          (result) => result,
          () => null,
        );
        const [ledger, accounts] = await Promise.all([ledgerPromise, accountsPromise]);
        if (token !== loadToken || !stillCurrent()) return;
        if (accounts !== null) rememberAccounts(accounts.accounts);
        paintHeader(ledger.header);
        paintJournal(ledger);
        return;
      }
      const accounts = await client.getLedgerAccounts(company, anchorPeriod());
      if (token !== loadToken || !stillCurrent()) return;
      rememberAccounts(accounts.accounts);
      paintHeader(accounts.header);
      if (view === "accounts") paintAccounts(accounts);
      else paintTrial(accounts);
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      clear(body);
      clear(pager);
      clear(bandHost);
      clear(alertHost);
      closeDrawer();
      await paintFailure(error, token);
    }
  }

  function loadInteractive(): void {
    void loadInner();
  }

  function refreshForFilters(): void {
    if (searchScope === "all") {
      if (allQuery.trim() === "") {
        showSearchPrompt();
        return;
      }
      runAllSearch(allQuery);
      return;
    }
    loadInteractive();
  }

  function setSearchScope(next: SearchScope): void {
    if (next === searchScope) return;
    searchScope = next;
    paintSearchScope(scopeControl, next);
    if (next === "all") {
      searchInput.value = allQuery;
      allSearchButton.hidden = false;
      hideSearchSummary();
      return;
    }
    searchInput.value = q;
    allSearchButton.hidden = true;
    hideSearchSummary();
    setSearchResultsMode(false);
    invalidateSearch();
    page = 1;
    loadInteractive();
  }

  function leaveAllSearchMode(): void {
    if (searchScope !== "all") return;
    searchScope = "month";
    paintSearchScope(scopeControl, "month");
    searchInput.value = q;
    allSearchButton.hidden = true;
    hideSearchSummary();
    setSearchResultsMode(false);
    invalidateSearch();
  }

  /** A search reply that arrives after this point must never paint. */
  function invalidateSearch(): void {
    loadToken += 1;
    searchInFlightKey = null;
    allSearchResult = null;
  }

  function setSearchResultsMode(active: boolean): void {
    bandHost.hidden = active;
    alertHost.hidden = active;
    refreshCopy.hidden = active;
    presets.hidden = active;
    periodControl.hidden = active;
    for (const field of exactFields) field.hidden = active;
  }

  function hideSearchSummary(): void {
    clear(searchSummary);
    searchSummary.hidden = true;
  }

  function showSearchSummary(text: string): void {
    clear(searchSummary);
    searchSummary.hidden = false;
    searchSummary.append(text);
  }

  function showSearchPrompt(): void {
    closeDrawer();
    clear(body);
    clear(pager);
    setSearchResultsMode(true);
    invalidateSearch();
    showSearchSummary(SEARCH_ALL_MONTHS_PROMPT);
  }

  function runAllSearch(committed?: string): void {
    const raw = committed ?? searchInput.value;
    const text = raw.trim();
    if (text === "") {
      allQuery = raw;
      showSearchPrompt();
      return;
    }
    allQuery = raw;
    const query = allSearchQuery(text);
    // Enter twice, or Enter then Search, is one search: the same request is
    // not sent again while it is still running. A different search is sent;
    // the token makes sure only the newest one paints.
    const key = JSON.stringify(query);
    if (searchInFlightKey === key) return;
    searchInFlightKey = key;
    const token = ++loadToken;
    closeDrawer();
    clear(body);
    clear(pager);
    setSearchResultsMode(true);
    showSearchSummary("Searching every month…");
    body.append(h("p", { class: "muted", "data-role": "books-search-loading" }, ["Searching every month…"]));
    void runAllSearchRequest(query, token);
  }

  async function runAllSearchRequest(
    query: LedgerSearchQuery,
    token: number,
  ): Promise<void> {
    try {
      const result = await client.searchLedger(company, query);
      if (token !== loadToken || !stillCurrent()) return;
      searchInFlightKey = null;
      allSearchResult = result;
      allSearchPage = 1;
      paintAllSearch(result);
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      searchInFlightKey = null;
      allSearchResult = null;
      clear(body);
      clear(pager);
      paintSearchFailure(error);
    }
  }

  function allSearchQuery(text: string): LedgerSearchQuery {
    // One search box across every month. The month view's exact filters are
    // hidden here, so they are not silently applied to this query.
    return { q: text };
  }

  function paintAllSearch(result: LedgerSearchResponse): void {
    clear(body);
    clear(pager);
    paintSearchSummary(result);
    if (result.items.length === 0) {
      // A normal-sized sentence in the results area, not a small grey line.
      body.append(emptyState(noMatchSentence(result)));
      return;
    }
    const start = (allSearchPage - 1) * JOURNAL_PAGE_SIZE;
    const hits = result.items.slice(start, start + JOURNAL_PAGE_SIZE);
    body.append(renderSearchTable(hits));
    renderSearchPager(result.items.length, !result.capped);
  }

  function renderSearchTable(hits: readonly LedgerSearchHit[]): HTMLElement {
    const head = h("tr", {}, [
      h("th", { scope: "col" }, ["Month"]),
      h("th", { scope: "col" }, ["Date"]),
      h("th", { scope: "col" }, ["Description"]),
      h("th", { scope: "col" }, ["Accounts"]),
      h("th", { scope: "col", class: "num" }, ["Amount"]),
    ]);
    const tbody = h("tbody", {});
    for (const hit of hits) tbody.append(searchHitRow(hit));
    const table = h(
      "table",
      { class: "data-table books-search-table", "aria-label": "Books search results" },
      [h("thead", {}, [head]), tbody],
    );
    return h("div", { class: "table-scroll", "data-role": "books-search-results" }, [table]);
  }

  function searchHitRow(hit: LedgerSearchHit): HTMLTableRowElement {
    const entry = hit.entry;
    const postings = entry.postings.map((posting) => posting.account);
    const labels = disambiguatedAccountLabels(postings, companies);
    const accounts = h("td", { class: "books-search-accounts" }, []);
    const seen = new Set<string>();
    for (const posting of entry.postings) {
      const label =
        labels.get(posting.account) ?? plainAccountLabel(accountText(posting.account), companies);
      if (seen.has(label)) continue;
      seen.add(label);
      if (accounts.childElementCount > 0) accounts.append(" · ");
      accounts.append(h("span", { "data-account": posting.account }, [label]));
    }
    if (accounts.childElementCount === 0) accounts.append("\u2014");
    const amount = entryAmount(entry.postings);
    const row = h(
      "tr",
      {
        class: "books-search-hit",
        tabindex: "0",
        "data-role": "books-search-hit",
        "data-period": hit.period,
        "data-entry-id": entry.id,
      },
      [
        h("td", { class: "books-search-month", "data-role": "search-result-month" }, [
          formatSearchMonth(hit.period),
        ]),
        h("td", { class: "books-search-date" }, [formatBooksDate(entry.date)]),
        h("td", { class: "books-search-description" }, [
          h("span", { class: "books-search-headline" }, [amVisibleDisplayText(entry.view.headline)]),
          entry.view.subline === undefined || entry.view.subline === ""
            ? null
            : h("span", { class: "books-search-subline" }, [
                amVisibleDisplayText(entry.view.subline),
              ]),
        ]),
        accounts,
        searchAmountCell(entry, amount),
      ],
    );
    const open = (): void => {
      const target: { period: string; q?: string } = { period: hit.period };
      const found = searchHitQuery(hit);
      if (found !== "") target.q = found;
      navigateTo("general-ledger", target);
    };
    row.addEventListener("click", open);
    row.addEventListener("keydown", (event) => {
      if (event.target !== row) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      open();
    });
    return row;
  }

  function renderSearchPager(total: number, complete: boolean): void {
    clear(pager);
    const totalPages = Math.max(1, Math.ceil(total / JOURNAL_PAGE_SIZE));
    const prev = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "search-prev",
        "aria-label": "Previous results page",
      },
      ["\u2039 Prev"],
    );
    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "search-next",
        "aria-label": "Next results page",
      },
      ["Next \u203a"],
    );
    if (allSearchPage <= 1) prev.disabled = true;
    if (allSearchPage >= totalPages) next.disabled = true;
    prev.addEventListener("click", () => {
      if (allSearchPage <= 1) return;
      allSearchPage -= 1;
      if (allSearchResult !== null) paintAllSearch(allSearchResult);
    });
    next.addEventListener("click", () => {
      if (allSearchPage >= totalPages) return;
      allSearchPage += 1;
      if (allSearchResult !== null) paintAllSearch(allSearchResult);
    });
    pager.append(
      prev,
      h("span", { class: "pager-info", "data-role": "search-page-info" }, [
        `Page ${String(allSearchPage)} of ${String(totalPages)} \u00b7 ${matchCountText(total, complete)}`,
      ]),
      next,
    );
  }

  function paintSearchSummary(result: LedgerSearchResponse): void {
    const parts: string[] = [];
    const from = result.searchedFrom === undefined ? null : formatSearchMonth(result.searchedFrom);
    const to = result.searchedTo === undefined ? null : formatSearchMonth(result.searchedTo);
    const unreadable = result.monthsUnreadable;
    if (from !== null && to !== null) {
      if (result.allMonthsSearched) {
        parts.push(`Searched every month (${from} to ${to}).`);
      } else {
        parts.push(`Searched ${from} to ${to}.`);
        // The Worker sends one flag for "not every month was searched". It is
        // false for an unreadable month inside the range too, so "older months"
        // is claimed only when nothing was unreadable and the flag is still false.
        if (unreadable === 0) parts.push(SEARCH_OLDER_NOT_SEARCHED);
      }
    } else {
      parts.push(result.allMonthsSearched ? SEARCHED_EVERY_MONTH : NOT_EVERY_MONTH_SEARCHED);
    }
    if (result.capped) {
      parts.push(SEARCH_NEWEST_MATCHES);
    } else if (result.items.length > 0) {
      parts.push(`${matchCountText(result.matchCount, !result.capped)}.`);
    }
    if (unreadable > 0) {
      parts.push(couldNotBeReadText(`${String(unreadable)} ${unreadable === 1 ? "month" : "months"}`));
    }
    showSearchSummary(parts.join(" "));
  }

  function paintSearchFailure(error: unknown): void {
    // The sentence is shown once, in the results area, with Try again. No
    // page-load heading: the page loaded, the search did not finish.
    hideSearchSummary();
    const retry = tryAgainButton(() => {
      runAllSearch(allQuery);
    });
    retry.className = "btn btn-primary";
    body.append(
      h("div", { class: "empty-state", role: "alert", "data-role": "books-search-error" }, [
        h("p", {}, [booksSearchErrorSentence(error, role)]),
        retry,
      ]),
    );
  }

  syncFilterVisibility();
  void client
    .getMe()
    .then((me) => {
      readerSlug = bookingSlug(me.identity);
    })
    .catch(() => {
      readerSlug = null;
    });
  await loadInner();
}

function searchScopeControl(
  name: string,
  selected: SearchScope,
  onChange: (next: SearchScope) => void,
): HTMLElement {
  const group = h("div", {
    class: "search-scope",
    role: "radiogroup",
    "aria-label": "Search range",
    "data-role": "search-scope",
  });
  for (const option of [
    { id: "month" as const, label: "This month" },
    { id: "all" as const, label: "All months" },
  ]) {
    const input = h("input", {
      type: "radio",
      name,
      value: option.id,
      "data-scope": option.id,
    });
    input.checked = option.id === selected;
    input.addEventListener("change", () => {
      if (input.checked) onChange(option.id);
    });
    group.append(h("label", { class: "search-scope-option" }, [input, option.label]));
  }
  return group;
}

function paintSearchScope(group: HTMLElement, selected: SearchScope): void {
  for (const input of group.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
    input.checked = input.value === selected;
  }
}

/** `2026-10` -> `Oct 2026`; anything unexpected is shown as safe text. */
function formatSearchMonth(period: string): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (match === null) return amVisibleDisplayText(period);
  const year = match[1];
  const month = SHORT_MONTHS[Number(match[2]) - 1];
  if (year === undefined || month === undefined) return amVisibleDisplayText(period);
  return `${month} ${year}`;
}

/** "1 match", "2 matches". */
function matchCountText(count: number, complete: boolean): string {
  return countedText(count, complete, "match", "matches");
}

/**
 * The zero-result sentence. "any month" is only said when every month was
 * searched (the Worker sends false whenever a month could not be read);
 * otherwise it names what was covered. The range is in the summary line.
 */
function noMatchSentence(result: LedgerSearchResponse): string {
  return result.allMonthsSearched && result.monthsUnreadable === 0
    ? NO_MATCHES_ANY_MONTH
    : NO_MATCHES_MONTHS_SEARCHED;
}

/** One amount for the entry: its larger side, which is both sides when balanced. */
function entryAmount(postings: readonly LedgerPosting[]): string {
  const sides = postingSides(postings);
  return centsOf(sides.credit) > centsOf(sides.debit) ? sides.credit : sides.debit;
}

/** One Amount, with the direction in plain words when the entry says which way cash moved. */
function searchAmountCell(entry: LedgerEntry, amount: string): HTMLElement {
  const delta = entry.view.cashDelta;
  const direction =
    delta === undefined || delta === ""
      ? ""
      : amVisibleDisplayText(delta).startsWith("-")
        ? "Money out"
        : "Money in";
  return h(
    "td",
    {
      class: "num books-search-amount",
      "data-role": "books-search-amount",
      "data-amount": amount,
      ...(direction === "" ? {} : { "data-direction": direction === "Money out" ? "out" : "in" }),
    },
    [
      h("span", { class: "books-search-amount-figure" }, [amount === "" ? "" : formatLedgerMoney(amount)]),
      direction === "" ? null : h("span", { class: "books-search-direction" }, [direction]),
    ],
  );
}

function postingSides(postings: readonly LedgerPosting[]): {
  debit: string;
  credit: string;
} {
  let debit = 0n;
  let credit = 0n;
  for (const posting of postings) {
    const sides = trialSides(posting.amount);
    debit += centsOf(sides.debit);
    credit += centsOf(sides.credit);
  }
  return { debit: centsToDecimal(debit), credit: centsToDecimal(credit) };
}

function searchHitQuery(hit: LedgerSearchHit): string {
  const plaid = plaidIdOrEmpty(hit.entry.ids);
  if (plaid !== "") return plaid;
  const moov = hit.entry.ids.moovId?.split(" ")[0] ?? "";
  if (moov !== "") return moov;
  const payee = hit.entry.payee?.trim() ?? "";
  if (payee !== "") return amVisibleDisplayText(payee);
  const narration = hit.entry.narration?.trim() ?? "";
  return narration === "" ? "" : amVisibleDisplayText(narration);
}

function booksSearchErrorSentence(error: unknown, role: StaffRole): string {
  if (error instanceof ConsoleDataError) {
    const reason = notPublishedReason(error);
    if (reason === "ledger_view_not_enabled") {
      return `${BOOKS_VIEW_OFF_TEXT} ${advice("switch_on_books_view", role)}`;
    }
    if (reason === "ledger_not_published") {
      return "No books have been saved for this company yet. Try again after the next booking run.";
    }
    // The statuses with a decided sentence read it from the one home (B4b-2);
    // the owner's 403 keeps decision 1's words and the account manager keeps
    // "Ask the owner."
    if (error.status === 401 || error.status === 403 || error.status === 429) {
      return failureCopy(error, { role, context: "read" }).sentence;
    }
  }
  // A search that did not finish is its own state, in the one shared wording.
  return SEARCH_COULD_NOT_FINISH;
}

function isBooksView(value: string | undefined): value is BooksView {
  return value === "journal" || value === "accounts" || value === "trial";
}

function isWalletAccount(account: AccountPath): boolean {
  return accountText(account)
    .split(":")
    .some((part) => part.toLowerCase() === "wallet");
}

function addDetail(list: HTMLElement, label: string, value: string | undefined): void {
  if (value === undefined || value.trim() === "") return;
  list.append(h("dt", {}, [label]), h("dd", {}, [amVisibleDisplayText(value)]));
}

function csvAmount(amount: string): string {
  if (amount === "") return "";
  if (DECIMAL_AMOUNT.test(amount)) return amount;
  return csvField(amount);
}

function centsOf(amount: string): bigint {
  if (amount === "") return 0n;
  return decimalToCents(amount) ?? 0n;
}

function centsToDecimal(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return `${negative ? "-" : ""}${String(abs / 100n)}.${String(abs % 100n).padStart(2, "0")}`;
}

function tryAgainButton(onTry: () => void): HTMLButtonElement {
  const button = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "retry", "aria-label": "Try again" },
    ["Try again"],
  );
  button.addEventListener("click", () => {
    onTry();
  });
  return button;
}

/** The shared error-state component says "Retry"; the console asks for "Try again". */
function relabelTryAgain(state: HTMLElement): void {
  const button = state.querySelector("button");
  if (button === null) return;
  button.textContent = "Try again";
  button.setAttribute("aria-label", "Try again");
}

